import { ExternalLink } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

export type ExternalSiteLink = {
  label: string;
  url: string;
  embeddable?: boolean;
};

type ExternalSiteDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  site: ExternalSiteLink | null;
};

const ExternalSiteDialog = ({ open, onOpenChange, site }: ExternalSiteDialogProps) => {
  if (!site) return null;

  const canEmbed = site.embeddable !== false;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="h-[85vh] w-[min(96vw,1100px)] max-w-5xl overflow-hidden border-primary/15 bg-background p-0">
        <div className="flex h-full min-h-0 flex-col">
          <DialogHeader className="gap-3 border-b border-primary/10 px-6 py-4 pr-12 text-left">
            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div className="space-y-1">
                <DialogTitle className="font-display text-xl text-foreground">{site.label}</DialogTitle>
                <DialogDescription className="font-body text-sm text-muted-foreground">
                  {canEmbed
                    ? "Le site s’ouvre dans une fenêtre intégrée au site."
                    : "Ce site bloque l’affichage intégré depuis une autre page."}
                </DialogDescription>
              </div>
              <a
                href={site.url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex shrink-0 items-center gap-2 rounded-full border border-primary/20 bg-secondary px-4 py-2 text-sm font-body text-foreground transition-colors hover:border-primary/40 hover:text-primary"
              >
                Ouvrir dans un nouvel onglet
                <ExternalLink size={14} />
              </a>
            </div>
          </DialogHeader>

          {canEmbed ? (
            <>
              <div className="border-b border-primary/10 px-6 py-2 text-xs font-body text-muted-foreground">
                Si la page reste vide, le partenaire bloque l’intégration : utilisez alors le bouton “Ouvrir le site”.
              </div>
              <iframe
                title={site.label}
                src={site.url}
                className="min-h-0 w-full flex-1 border-0 bg-background"
                referrerPolicy="strict-origin-when-cross-origin"
              />
            </>
          ) : (
            <div className="flex flex-1 items-center justify-center px-6">
              <div className="max-w-md space-y-4 text-center">
                <p className="font-display text-2xl text-foreground">Affichage intégré indisponible</p>
                <p className="font-body text-sm leading-relaxed text-muted-foreground">
                  {site.label} n’autorise pas l’ouverture dans une popup intégrée. Vous pouvez toutefois accéder au site directement.
                </p>
                <a
                  href={site.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-3 font-body text-sm text-primary-foreground transition-opacity hover:opacity-90"
                >
                  Ouvrir dans un nouvel onglet
                  <ExternalLink size={14} />
                </a>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ExternalSiteDialog;
